<template>
  <div class="exhibition">
    <div class="exhibition__wrapper">
      <div
        class="exhibition__background"
        :style="{
          backgroundImage: `url(${exhibition.header_image})`,
        }"
      ></div>
      <div class="exhibition__content">
        <div class="exhibition__details exhibition__row">
          <div class="exhibition__details-box exhibition__column exhibition__column--three">
            <h2 class="exhibition__details-box_title">{{ exhibition.title }}</h2>
            <div
              class="exhibition__details-box_details collect-editor collect-editor--white"
              v-html="exhibition.description"
            ></div>
            <BaseButton
              v-if="exhibition.button_url"
              class="button button--xs button--outline-white button--uppercase"
              :text="exhibition.button_text || 'click to go'"
              @click="changeLocation(exhibition.button_url)"
            />
          </div>
          <div class="exhibition__info-box exhibition__column exhibition__column--one">
            <div class="exhibition__info-box_section">
              <template v-if="exhibition.type === 'Online'">
                <h3>
                  Exhibition
                  {{ exhibitionEnd ? "has ended:" : exhibitionStart ? "ends in:" : "opens in:" }}
                </h3>
                <p class="exhibition__info-box_time">
                  {{
                    exhibitionEnd
                      ? $moment(exhibition.end_date).format("MMMM Do")
                      : exhibitionStart
                      ? $moment(exhibition.end_date).format("MMMM Do")
                      : $moment(exhibition.start_date).format("MMMM Do")
                  }}
                </p>
              </template>
              <template v-else>
                <h3>
                  Exhibition
                  {{ exhibitionEnd ? "has ended:" : exhibitionStart ? "ends in:" : "opens in:" }}
                </h3>
                <p v-if="time !== '00d:00h:00m:00s'" class="exhibition__info-box_time">
                  {{ time }}
                </p>
                <p v-else class="exhibition__info-box_end-date">
                  Event ended on {{ $moment(exhibition.end_date).format("MMMM Do, YYYY") }}
                </p>
              </template>
            </div>

            <div class="exhibition__info-box_section">
              <h3>Exhibition info:</h3>
              <div class="exhibition__info-box_section_info">
                <p>{{ exhibition.type }}</p>
                <p>{{ exhibition.address }}</p>
              </div>
            </div>

            <div v-if="exhibition.schedule" class="exhibition__info-box_section">
              <h3>Gallery Dates/Hours:</h3>
              <div class="exhibition__info-box_section_info">
                <div
                  v-html="exhibition.schedule"
                  class="collect-editor collect-editor--white"
                ></div>
              </div>
            </div>

            <div v-if="exhibition.safety_protocol" class="exhibition__info-box_section">
              <h3>Safety Protocol:</h3>
              <div
                v-html="exhibition.safety_protocol_info"
                class="collect-editor collect-editor--white"
              ></div>
            </div>

            <div
              v-if="time !== '00d:00h:00m:00s' && dates.length"
              class="exhibition__info-box_buttons"
            >
              <BaseButton
                class="button button--full-width button--white"
                text="SCHEDULE YOUR VISIT"
                @click="toggleModal"
              />
              <BaseButton
                v-if="exhibition.catalog_button_url"
                class="button button--xs button--outline-white button--uppercase"
                :text="exhibition.catalog_button_text || 'catalog'"
                @click="changeLocation(exhibition.catalog_button_url)"
              />
            </div>
          </div>
        </div>
        <div class="exhibition__details_video" v-if="exhibition.videos && exhibition.videos.url">
          <CustomVideo :src="exhibition.videos.url" />
          <p v-if="exhibition.videos.caption">{{ exhibition.videos.caption }}</p>
        </div>
        <template v-if="feature">
          <SpecificExhibitionTitleBox
            :tagsData="tagsData"
            :sortDataArts="sortDataArts"
            :sortDataArtists="sortDataArtists"
            :filters="filters"
            :search="search"
            :cardData="cardData"
            @changeSort="changeSort"
            @sortExhibition="sortExhibition"
            @update:openFilters="openFilters = $event"
            @selectFilter="selectFilter"
            @applyFilters="refreshSelectedFilters"
            @clearFilters="refreshSelectedFilters(true)"
            @changeSearch="changeSearch"
          />

          <div v-if="features.length > 0" class="exhibition__exhibitions-box">
            <div
              class="exhibition__exhibitions-box_card"
              v-for="(item, key) in features"
              :key="`${key}-item`"
              @click="goToFeature(item.slug)"
            >
              <div class="image-box">
                <img class="image" :src="item.search_image" />
                <div class="hover-container">Click to View</div>
              </div>
              <h3 class="title">{{ item.title || item.name }}</h3>
              <span class="subtitle">{{ item.artist || item.region }}</span>
            </div>
          </div>
          <h6 class="exhibition__empty" v-else>No {{ cardData.key }}</h6>

          <div class="exhibition__button-box" v-if="showLoadMoreButton && hasMoreFeatures">
            <BaseButton
              class="button button--lg button--white"
              text="LOAD MORE"
              @click.native="handleLoadMoreClick"
            />
          </div>
        </template>

        <div class="exhibition__catalog-box">
          <div class="exhibition__catalog-box_container">
            <h2 class="exhibition__catalog-box_title" v-if="exhibition.catalog_title">
              {{ exhibition.catalog_title }}
            </h2>
            <div
              class="collect-editor collect-editor--white exhibition__catalog-box_details"
              v-if="exhibition.catalog_description"
              v-html="exhibition.catalog_description"
            ></div>
            <BaseButton
              v-if="exhibition.catalog_button_text"
              class="button button--xs button--white button--uppercase"
              :text="exhibition.catalog_button_text"
              @click="changeLocation(exhibition.catalog_button_url)"
            />
          </div>
          <div
            v-if="exhibition.catalog_image"
            class="exhibition__catalog-box_image"
            :style="{
              backgroundImage: `url(${exhibition.catalog_image})`,
            }"
          ></div>
        </div>

        <div class="exhibition__sponsors-box exhibition__row">
          <div
            class="exhibition__sponsors-box_sponsors exhibition__column"
            v-if="exhibition.sponsors.length > 0"
          >
            <h3 class="title">Sponsors</h3>
            <div class="container">
              <a
                v-for="(item, key) in exhibition.sponsors"
                :key="`sponsor-${key}`"
                class="container__item sponsor-link"
                :class="{ 'sponsor-link--no-clickable': !item.url }"
                :href="item.url ? item.url : ''"
                target="_blank"
                @click.prevent="changeLocation(item.url)"
              >
                <span class="container__item_box">
                  <img class="container__item_logo" :src="item.image" alt="Sponsor logo"
                /></span>
                <span>{{ item.caption }}</span>
              </a>
            </div>
          </div>
          <div v-if="artistsList.length > 0" class="artists">
            <h4 class="artists__title">Artists</h4>

            <div class="artists__body">
              <div
                v-for="(column, index) in artistsList"
                :key="`column-${index}`"
                class="artists__body_column"
              >
                <router-link
                  v-for="(item, index) in column"
                  :key="`item-${index}`"
                  class="artists__item"
                  :class="{ 'artists__item--disabled': !item.hasArtworks }"
                  :to="{ path: `/artists/${item.slug}` }"
                >
                  {{ item.title }}
                </router-link>
              </div>
            </div>
          </div>

          <div class="exhibition__sponsors-box_contacts exhibition__column exhibition__column--one">
            <h3 class="title">Contact Info</h3>
            <div v-for="(item, key) in contacts" :key="`contact-${key}`" class="contact-item">
              <span class="item-title">{{ item.title }}: </span>
              <a class="item-contact" :href="item.href">{{ item.contact }}</a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <CreateReservation
      v-if="modal"
      :item="exhibition"
      :modal="modal"
      :dates="dates"
      :timeslots="timeslots"
      :isExhibition="true"
      @hideModal="toggleModal"
    />
    <div ref="infiniteTrigger" style="height: 1px"></div>
  </div>
</template>

<script>
import BaseButton from "@/components/buttons/BaseButton.vue";
import PublicSelect from "@/components/inputs/PublicSelect.vue";
import Tag from "@/components/Tag.vue";
import FilterBlock from "@/components/FilterPublic.vue";
import { mapActions, mapGetters, mapMutations } from "vuex";
import CustomVideo from "@/components/CustomVideo.vue";
import CreateReservation from "./components/CreateReservation.vue";
import { sortedColumns } from "@/helper.js";
import useInfiniteScroll from "@/composables/useInfiniteScroll.js";
import SpecificExhibitionTitleBox from "@/views/public/events/components/SpecificExhibitionTitleBox.vue";
import useSeoHead from "@/composables/useSeoHead";

export default {
  components: {
    BaseButton,
    PublicSelect,
    Tag,
    FilterBlock,
    CustomVideo,
    CreateReservation,
    SpecificExhibitionTitleBox,
  },

  data() {
    return {
      time: "00d:00h:00m:00s",
      timer: null,
      exhibitionStart: false,
      exhibitionEnd: false,
      tagsData: [
        { id: "art", title: "Art" },
        { id: "artist", title: "Artists" },
      ],
      sortDataArts: [
        { id: "title_a_z", title: "Art (A-Z)" },
        { id: "title_z_a", title: "Art(Z-A)" },
        { id: "last_name_a_z", title: "Artist (A-Z)" },
        { id: "last_name_z_a", title: "Artist (Z-A)" },
      ],
      sortDataArtists: [
        { id: "last_name_a_z", title: "Artist (A-Z)" },
        { id: "last_name_z_a", title: "Artist (Z-A)" },
      ],
      cardData: {
        slug: null,
        materials: "",
        price_start: "",
        price_end: "",
        colors: "",
        region: "",
        sort: "title_a_z",
        page: 1,
        key: "art",
      },
      openFilters: false,
      contacts: [
        { title: "email", contact: "art@browngrotta.com", href: "mailto:art@browngrotta.com" },
        { title: "phone", contact: "203-834-0623", href: "tel:2038340623" },
        { title: "fax", contact: "203-762-5981", href: "fax:2037625981" },
      ],
      modal: false,
      artistsList: [],
      showLoadMoreButton: true,
      loading: false,
    };
  },
  head() {
    const title = this.exhibition?.title ?? "Exhibition browngrotta arts";
    const description = this.exhibition?.description ?? "Description Exhibition browngrotta arts";
    const image = this.exhibition?.header_image ?? "/img/icons/favicon-500x500.png";

    return useSeoHead({
      title,
      description: `${title} - ${description}`,
      image,
      routePath: this.$route.path,
    });
  },
  computed: {
    ...mapGetters("publicapi/exhibitions", {
      exhibition: "getExhibition",
      feature: "getFeature",
      features: "getFeatures",
      hasMoreFeatures: "getHasMoreFeatures",
      dates: "getTimeslotDatesAvailability",
      timeslots: "getTimeslotAvailability",
    }),
    ...mapGetters("publicapi/filters", {
      filters: "getFilters",
      search: "getSearch",
      selectedFilters: "getSelectedFilters",
    }),
    getColumnsAmount() {
      const width = window.innerWidth;

      if (width < 540) {
        return 1;
      } else if (width < 768) {
        return 2;
      } else {
        return 3;
      }
    },
  },
  async created() {
    this.cardData.slug = this.$route.params.slug;
    await this.apiGetSpecificExhibition(this.$route.params.slug);

    if (this.exhibition.id) {
      await this.apiGetExhibitionTimeslotDaysAvailability(this.exhibition.id);
    }

    this.artistsList = sortedColumns(this.exhibition.artists || [], this.getColumnsAmount); //to get columns

    this.$emit("updateHead");
    await this.getItemsForFilters(this.cardData.key, this.search, true);
    await this.apiGetFeatures(this.cardData);
    const breadcrumbs = [
      {
        title: "Home",
        to: "/",
      },
      {
        title: "Exhibitions",
        to: "/exhibitions",
        clickable: true,
      },
      {
        title: `${this.exhibition.title}`,
        clickable: false,
      },
    ];
    this.setBreadcrumbs(breadcrumbs);

    this.countDown();
    this.timer = setInterval(() => this.countDown(), 1000);

    this.setShareData({
      title: this.exhibition.title,
      description: this.exhibition.description,
      image: this.exhibition.header_image,
    });
  },

  destroyed() {
    if (this.timer != null) {
      clearInterval(this.timer);
    }
  },
  methods: {
    ...mapMutations(["setBreadcrumbs"]),
    ...mapMutations("publicapi/share", ["setShareData"]),
    ...mapActions("publicapi/exhibitions", [
      "apiGetSpecificExhibition",
      "apiGetFeatures",
      "apiGetExhibitionTimeslotAvailability",
      "apiGetExhibitionTimeslotDaysAvailability",
    ]),
    ...mapActions("publicapi/filters", ["apiGetFiltersBlock"]),
    ...mapMutations("publicapi/filters", ["updateFilters", "updateSelectedFilters", "clear"]),
    countDown() {
      const dateFormat = "YYYY-MM-DD HH:mm:ss";

      const currentTime = this.$moment().tz("America/New_York").format(dateFormat);
      const eventStart = this.$moment(this.exhibition.start_date).format(dateFormat);
      const eventEnd = this.$moment(this.exhibition.end_date).format(dateFormat);

      if (currentTime > eventEnd) {
        this.exhibitionEnd = true;
      } else if (currentTime > eventStart) {
        this.exhibitionStart = true;
        this.contTime(eventEnd, currentTime);
      } else if (currentTime < eventStart) {
        this.exhibitionStart = false;
        this.contTime(eventStart, currentTime);
      }
    },
    contTime(start, end) {
      let seconds = this.$moment(start).diff(end, "seconds");
      if (seconds < 0) {
        seconds = 0;
      }
      let d = Math.floor(seconds / (3600 * 24));
      let h = Math.floor((seconds % (3600 * 24)) / 3600);
      let m = Math.floor((seconds % 3600) / 60);
      let s = Math.floor(seconds % 60);

      let dDisplay = d < 10 ? `0${d}` : d;
      let hDisplay = h < 10 ? `0${h}` : h;
      let mDisplay = m < 10 ? `0${m}` : m;
      let sDisplay = s < 10 ? `0${s}` : s;

      this.time = `${dDisplay}d:${hDisplay}h:${mDisplay}m:${sDisplay}s`;
    },
    async getItemsForFilters(val, search, isFirst) {
      let filterBlock = {
        list: "materials,price,colors",
        type: "collection",
      };
      switch (val) {
        case "art": {
          await this.apiGetFiltersBlock({ ...filterBlock, search, isFirst });
          break;
        }
        case "artist": {
          await this.apiGetFiltersBlock({ ...filterBlock, list: "regions", search, isFirst });
          break;
        }
      }
    },
    async changeSort(id) {
      let data = { ...this.cardData, key: id };
      await this.getItemsForFilters(id, this.search, true);
      switch (id) {
        case "art": {
          data = { ...data, sort: "title_a_z" };
          break;
        }
        case "artist": {
          data = { ...data, sort: "last_name_a_z" };
          break;
        }
      }
      let success = await this.apiGetFeatures(data);
      if (success) {
        this.cardData = { ...data };
      }
    },

    async changeSearch(search) {
      await this.getItemsForFilters(this.cardData.key, search);
    },
    async sortExhibition({ id }) {
      let data = { ...this.cardData, sort: id };
      let success = await this.apiGetFeatures(data);
      if (success) {
        this.cardData = { ...data };
      }
    },
    selectFilter(val) {
      this.updateFilters(val);
      if (val.apply) {
        this.refreshSelectedFilters();
      }
    },
    async refreshSelectedFilters(clear) {
      if (clear) {
        await this.clear();
      }
      await this.updateSelectedFilters();
      this.filterTable();
    },
    async filterTable() {
      let data = { ...this.cardData, page: 1 };
      this.selectedFilters.forEach(el => {
        if (el.key === "price") {
          data.price_start = "";
          data.price_end = "";
          el.items.forEach(i => {
            data[i.key] = i.val;
          });
        } else {
          let filters = el.items.map(i => {
            return i.id;
          });
          data[el.key] = filters.join(",");
        }
      });
      let success = await this.apiGetFeatures(data);
      if (success) {
        this.cardData = data;
      }
    },
    async loadMore() {
      if (this.loading || !this.hasMorePages) return;
      this.loading = true;

      let data = { ...this.cardData, page: this.cardData.page + 1 };
      let success = await this.apiGetFeatures(data);
      if (success) {
        this.cardData = data;
      }
      this.loading = false;
    },
    handleLoadMoreClick() {
      this.showLoadMoreButton = false;
      this.loadMore();
      this.infiniteScroll.enableInfiniteScroll();
    },

    handleScroll() {
      const scrollPosition = window.innerHeight + window.scrollY;
      const bottomPosition = document.documentElement.scrollHeight;

      if (scrollPosition >= bottomPosition - 100) {
        this.loadMore();
      }
    },
    goToFeature(slug) {
      let url = this.cardData.key === "art" ? `/artworks/${slug}` : `/artists/${slug}`;
      this.$router.push({ path: url });
    },
    changeLocation(url) {
      if (url) window.open(url, "_blank");
    },
    toggleModal() {
      this.modal = !this.modal;
    },
  },
  mounted() {
    this.infiniteScroll = useInfiniteScroll(this);
    if (!this.showLoadMoreButton) {
      this.infiniteScroll.enableInfiniteScroll();
    }
  },
  beforeDestroy() {
    this.infiniteScroll.disableInfiniteScroll();
  },
};
</script>

<style lang="scss" scoped>
* {
  color: $white;
}

.exhibition {
  display: flex;

  &__wrapper {
    @include fullScreenWrapper;
  }

  &__background {
    @include backgroundBlock();
  }

  &__content {
    position: relative;
    width: 100%;
    margin-top: 40rem;
    padding: 0rem 13.7rem 7.4rem;
    z-index: 2;
    @media screen and (max-width: $md) {
      margin-top: 24.5rem;
      padding: 0rem 8rem 7.4rem;
    }
    @media screen and (max-width: $sm) {
      padding: 0rem 3rem 7.4rem;
    }
    @media screen and (max-width: $xs) {
      padding: 0rem 25px 11rem;
    }
    .exhibition__row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 130px;
      @media screen and (max-width: $md) {
        flex-wrap: wrap;
        gap: 50px;
      }
      &--align-center {
        align-items: center;
      }
    }
    .exhibition__column {
      width: 100%;
      &--one {
        max-width: 345px;
        padding: 18px 16px;
        border: 1px solid rgba(137, 137, 137, 1);
        backdrop-filter: blur(18.399999618530273px);
        border-radius: 12px;
        @media screen and (max-width: $md) {
          max-width: 100%;
        }

        .contact-item {
          font-size: 15px;
          margin-bottom: 4px;
        }
      }
    }
  }
  &__details {
    &_video {
      padding: 80px 0;
      padding: 80px 0;
      @media screen and (max-width: $sm) {
        padding: 4rem 0;
      }
      @media screen and (max-width: $xs) {
        padding: 4rem 0;
      }
      p {
        font-size: 1.8rem;
        font-weight: 300;
        line-height: 1.66;
        color: $white;
        text-align: center;
        padding: 14px;
      }
    }
  }

  &__details-box {
    display: flex;
    flex-direction: column;
    &_title {
      font-size: 5.2rem;
      line-height: 1.55;
      margin-bottom: 30px;
      @media screen and (max-width: $md) {
        font-size: 35px;
        margin-bottom: 20px;
      }
    }

    .button {
      margin-top: 2.2rem;
      @media screen and (max-width: $xs) {
        margin-top: 3.5rem;
      }
    }
  }

  &__info-box {
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: 2.5rem 0rem 0rem 0;
    @media screen and (max-width: $md) {
      width: 100%;
      padding: 9.5rem 0rem 0rem 0rem;
    }

    &_buttons {
      width: 100%;
      display: flex;
      gap: 1.5rem;
    }

    &_section {
      &:not(:last-child) {
        border-bottom: 0.6px solid rgba(137, 137, 137, 1);
        margin-bottom: 12px;
        padding-bottom: 12px;
      }
      h3 {
        font-size: 18px;
        line-height: 1.33;
        font-weight: 700;
        margin-bottom: 10px;
      }
      p {
        font-size: 16px;
        line-height: 1.33;
        font-weight: 400;
        margin-bottom: 8px;
      }
      &_info {
        display: flex;
        flex-direction: column;
      }
    }

    &_time {
      font-size: 28px;
      font-weight: 400;
      line-height: 1.75;
    }

    &_end-date {
      font-size: 36px;
      line-height: 1.3;
    }

    li {
      span {
        font-size: 16px;
        font-weight: 700;
        margin-left: -12px;
        @media not all and (min-resolution: 0.001dpcm) {
          @supports (-webkit-appearance: none) {
            margin-left: 0px;
          }
        }
        @supports (-moz-appearance: none) {
          margin-left: 0px;
        }
      }
    }
  }

  &__empty {
    @include emptyMessage(10px 10px 50px);
  }

  &__exhibitions-box {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(3, minmax(30%, 1fr));
    grid-column-gap: 3rem;
    grid-row-gap: 7rem;
    @media screen and (max-width: $md) {
      grid-template-columns: repeat(2, minmax(30%, 1fr));
    }
    @media screen and (max-width: $xs) {
      grid-template-columns: minmax(30%, 1fr);
      grid-row-gap: 7rem;
    }

    &_card {
      @include flexColumnClickable;

      &:hover {
        .image-box {
          .hover-container {
            opacity: 1;
          }
        }
      }

      .image-box {
        position: relative;

        .image {
          @include imageContainer();
          @media screen and (max-width: $xs) {
            height: 90vw;
          }
        }

        .hover-container {
          @include imagePreviewHover;
        }
      }

      .title {
        font-size: 2.4rem;
        line-height: 3.1rem;
        margin-top: 1rem;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        line-clamp: 2;
        text-overflow: ellipsis;
        overflow: hidden;
      }

      .subtitle {
        margin-top: 0.5rem;
        font-size: 1.4rem;
        line-height: 2.5rem;
        text-transform: uppercase;
        @media screen and (max-width: $xs) {
          margin-top: 1rem;
          font-size: 1.6rem;
          line-height: 2.8rem;
        }
      }
    }
  }

  &__button-box {
    @include centeredButtonBox(10rem);
    @media screen and (max-width: $md) {
      margin-bottom: 7rem;
    }
  }

  &__catalog-box {
    display: flex;
    justify-content: space-between;
    padding-top: 3.5rem;
    @media screen and (max-width: $sm) {
      flex-direction: column;
    }
    &_container {
      width: 49%;
      display: flex;
      justify-content: center;
      flex-direction: column;
      @media screen and (max-width: $sm) {
        width: 100%;
      }

      .button {
        min-width: 180px;
        margin-top: 2.5rem;
        @media screen and (max-width: $xs) {
          margin-top: 3rem;
        }
      }
    }
    &_title {
      font-size: 5.2rem;
      line-height: 1.25;
      margin-bottom: 14px;
      @media screen and (max-width: $md) {
        font-size: 35px;
      }
    }
    &_image {
      height: 65rem;
      width: 65rem;
      background-size: cover;
      background-position: center;
      @media screen and (max-width: $xl) {
        height: 55rem;
        width: 55rem;
      }
      @media screen and (max-width: $lg) {
        height: 45rem;
        width: 45rem;
      }
      @media screen and (max-width: $md) {
        height: 35rem;
        width: 35rem;
      }
      @media screen and (max-width: $sm) {
        margin-top: 50px;
      }
      @media screen and (max-width: $xs) {
        height: 25rem;
        width: 100%;
      }
    }
  }

  &__sponsors-box {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-around;
    margin-top: 17.6rem;
    @media screen and (max-width: $lg) {
      justify-content: flex-start;
      margin-top: 15rem;
    }
    @media screen and (max-width: $md) {
      margin-top: 10rem;
    }
    @media screen and (max-width: $xs) {
      margin-top: 7.5rem;
    }

    .title {
      font-size: 4rem;
      line-height: 1.3;
      margin-bottom: 20px;
    }

    &_sponsors {
      @media screen and (max-width: $md) {
        margin-bottom: 4rem;
      }

      .container {
        width: calc(100% + 30px);
        margin-left: -15px;
        display: flex;
        flex-wrap: wrap;

        &__item {
          max-width: 23.4rem;
          margin: 0 15px 30px;
          &_box {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 23.4rem;
            width: 23.4rem;
            border-radius: 17px;
            overflow: hidden;
            cursor: pointer;
            text-decoration: none;
            border: 1px solid $white;
            margin-bottom: 10px;
          }
        }
      }
      .sponsor-link {
        text-decoration: none;
        font-size: 18px;
        &--no-clickable {
          cursor: default;
        }
      }
    }

    &_contacts {
      .item-title,
      .item-contact {
        font-weight: 500;
      }
      .item-contact {
        margin-left: 18px;
      }
    }
  }
}
.artists {
  width: 100%;
  &__title {
    font-size: 40px;
    line-height: 1.2;
    margin-bottom: 24px;
  }
  &__body {
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    @media only screen and (max-width: $xs) {
      display: block;
      text-align: center;
    }
    &_column {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: 8px;
    }
  }

  &__item {
    font-size: 18px;
    font-weight: 300;
    line-height: 1.1;
    text-decoration: none;
    cursor: pointer;
    transition: all 0.3s ease;
    text-align: left;
    &--disabled {
      color: $grey;
    }
    &:hover {
      text-decoration: underline;
    }
    @media only screen and (max-width: $xs) {
      margin-bottom: 4px;
    }
  }
}
</style>

